import React from 'react';
import {
    IFiltersProps,
    TFilterFieldType,
    IFilterOption,
    IFilter
} from './HBFilters.types';
import { FiltersProvider } from './HBFilters.context';
import FilterPopover from './components/FilterPopover';
import MultiSelectFilter from './components/MultiSelectFilter';
import SingleSelectFilter from './components/SingleSelectFilter';
import AddFilterMenu from './components/AddFilterMenu';
import HBLinkButton from '../HBLinkButton/HBLinkButton';
import { StyledFilterItem } from './components/PopoverBase';
import styled from 'styled-components';
import { STRUCTURE } from './utils/structure';
import { useFilters } from './hooks/useFilters';

// -------------------------------- Components -------------------------------- //

const FilterChip = ({ filter }: { filter: IFilter }) => {
    const { field, value } = filter;
    return (
        <FilterPopover key={field.id} filter={filter}>
            {field.type === TFilterFieldType.MultiSelect ? (
                <MultiSelectFilter
                    field={field}
                    value={value as IFilterOption[]}
                />
            ) : (
                <SingleSelectFilter
                    field={field}
                    value={value as IFilterOption | null}
                />
            )}
        </FilterPopover>
    );
};

const ClearFiltersButton = () => {
    const { removeAll, texts } = useFilters();

    return (
        <StyledFilterItem>
            <HBLinkButton
                testIdName="remove-all-filters"
                onClick={removeAll}
                size="sm"
                linkButtonTheme="neutral"
            >
                {texts.removeAllFilters}
            </HBLinkButton>
        </StyledFilterItem>
    );
};

const Filters = () => {
    const { activeFilters, availableFieldsToAdd } = useFilters();

    return (
        <StyledFilterContainer>
            <StyledFilterContent>
                {activeFilters.map(filter => (
                    <FilterChip key={filter.id} filter={filter} />
                ))}
                <AddFilterMenu availableFields={availableFieldsToAdd} />
            </StyledFilterContent>
            {!!activeFilters.length && <ClearFiltersButton />}
        </StyledFilterContainer>
    );
};

/**
 * HBFilters is the main entry point for the filter bar component.
 * It wraps the filter UI with the FiltersProvider, which manages all filter state and logic.
 */
const HBFilters = ({
    availableFields,
    initialFilters,
    onChange,
    onAddFilter,
    onDeleteFilter,
    onFilterActivated,
    onRemoveAll,
    texts
}: IFiltersProps) => (
    <FiltersProvider
        availableFields={availableFields}
        initialFilters={initialFilters}
        onChange={onChange}
        texts={texts}
        onAddFilter={onAddFilter}
        onDeleteFilter={onDeleteFilter}
        onFilterActivated={onFilterActivated}
        onRemoveAll={onRemoveAll}
    >
        <Filters />
    </FiltersProvider>
);

// -------------------------------- Styles -------------------------------- //

const StyledFilterContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: ${STRUCTURE.gapBetweenChipsAndClear};
`;

const StyledFilterContent = styled.div`
    display: flex;
    gap: ${STRUCTURE.gapBetweenFilterChips};
    align-items: center;
    flex-wrap: wrap;
    flex: 1;
`;

export default HBFilters;
